import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from '@framework/i18n';
import {
  Button,
  Flex,
  Popconfirm,
  Space,
  Tooltip,
  type ButtonProps,
  type PopconfirmProps,
} from 'antd';
import { type SizeType } from 'antd/es/config-provider/SizeContext';
import { type ButtonType } from 'antd/lib/button';
import FocusTrap from 'focus-trap-react';
import React, { useId, useRef, useState } from 'react';

interface DetachableEvent {
  preventDefault(): void;
  stopPropagation(): void;
}

const detachEvent = (e?: DetachableEvent) => {
  if (!e) return;
  e.preventDefault();
  e.stopPropagation();
};

export interface ConfirmButtonProps
  extends Omit<PopconfirmProps, 'title' | 'onConfirm'> {
  size?: SizeType;
  buttonType?: ButtonType;
  buttonIcon?: React.ReactNode;
  iconOnlyButton?: boolean;
  buttonDanger?: boolean;
  disableConfirmButton?: boolean;
  loading?: boolean;
  popconfirmButtonDanger?: boolean;
  title?: React.ReactNode;
  tooltip?: string;
  onConfirm?: (e: React.MouseEvent<HTMLElement>) => void | Promise<void>;
  buttonProps?: ButtonProps;
  as?: React.ComponentType<ButtonProps>;
}

export function ConfirmButton(props: Readonly<ConfirmButtonProps>) {
  const {
    size = 'small',
    buttonType = 'primary',
    buttonIcon,
    iconOnlyButton,
    buttonDanger,
    disableConfirmButton,
    loading,
    popconfirmButtonDanger = true,
    title,
    tooltip,
    // PopConfirm props
    children,
    disabled,
    onConfirm,
    // La spostiamo nel FocusTrap manualmente
    description,
    as,
    ...rest
  } = props;

  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const popoverId = useId(); // genera un ID unico

  const ButtonComponent = props.as ?? Button;

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // PopConfirm props
  const popConfirmProps = {
    // Fondamentale per gestire il FocusTrap
    destroyTooltipOnHide: true,
    // Spostiamo tutto il contenuto del Popconfirm in un FocusTrap, compresi
    // i bottoni di conferma e annulla.
    title: (
      <div id={popoverId}>
        <FocusTrap
          active={confirmOpen}
          focusTrapOptions={{
            escapeDeactivates: () => {
              setConfirmOpen(false);
              return true;
            },
            fallbackFocus: () => containerRef.current!,
            setReturnFocus: previous => {
              return previous ?? buttonRef.current!;
            },
          }}
        >
          <div ref={containerRef}>
            <Flex vertical align="flex-end" gap={8}>
              <div>
                {props.title ??
                  t(
                    'button.confirm-button.title',
                    "Sei sicuro di voler continuare con l'operazione?",
                  )}
              </div>
              {props.description && (
                <div>
                  {typeof props.description === 'function'
                    ? props.description()
                    : props.description}
                </div>
              )}
              <Space>
                <Button
                  type="default"
                  size="small"
                  onClick={() => setConfirmOpen(false)}
                  tabIndex={0}
                >
                  {t('button.confirm-button.cancel', 'Annulla')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={e => {
                    detachEvent(e);
                    onConfirm?.(e);
                  }}
                  tabIndex={0}
                  danger={popconfirmButtonDanger}
                  disabled={disableConfirmButton}
                >
                  {t('button.confirm-button.confirm', 'Conferma')}
                </Button>
              </Space>
            </Flex>
          </div>
        </FocusTrap>
      </div>
    ),
    placement: 'topRight',
    // Cancelliamo i pulsanti di default; li abbiamo spostati nel FocusTrap
    okButtonProps: {
      style: { display: 'none' },
      tabIndex: -1,
    },
    cancelButtonProps: {
      style: { display: 'none' },
      tabIndex: -1,
    },
    onConfirm: () => {},
    onCancel: () => {},
    children,
    disabled,
    icon: <WarningOutlined aria-hidden="true" />,
    open: confirmOpen,
    onOpenChange: setConfirmOpen,
    rootClassName: 'confirm-button__popconfirm',
    ...rest,
  } satisfies PopconfirmProps;

  const button = (
    <ButtonComponent
      {...props.buttonProps}
      ref={buttonRef}
      title={typeof title === 'string' ? title : undefined}
      disabled={disabled}
      type={buttonType}
      icon={buttonIcon}
      size={size}
      loading={loading}
      danger={buttonDanger}
      onClick={() => setConfirmOpen(true)}
      aria-expanded={confirmOpen}
      aria-haspopup="dialog"
      aria-controls={popoverId}
    >
      {iconOnlyButton ? '' : children ?? popConfirmProps.okText}
    </ButtonComponent>
  );

  return (
    <Tooltip title={tooltip && !confirmOpen ? tooltip : ''}>
      <Popconfirm {...popConfirmProps}>{button}</Popconfirm>
    </Tooltip>
  );
}
